import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import ApiPath from './ConstantApi';
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";

export interface ServiceFeeData {
  profileName: string;
  profileCode: string;
  isActive: boolean;
  description: string;
  validFrom?: string;
  validTo?: string;
  waveOff?: boolean;
  promotedFrom?: string;
  promotedTo?: string;
  rules: any[];
  vatOnServiceFee?: string;
  // products: string[];
}

export interface CreateAndUpdateServiceFeeRequest {
  id?: string;
  data: ServiceFeeData;
}

export interface ServiceFeeResponse {
  status: string;
  message: string;
  data?: any;
}

export interface ModuleFieldsResponse {
  status: string;
  message: string;
  data: {
    fields: string[];
  };
}

// Define cache tags type
export const SERVICE_FEE_TAG = 'ServiceFee' as const;
export const MODULE_FIELDS_TAG = 'ModuleFields' as const;

type Tags = typeof SERVICE_FEE_TAG | typeof MODULE_FIELDS_TAG;

export const ServiceFeeService = createApi({
  reducerPath: 'ServiceFee',
  baseQuery: RtkBaseQueryWithReauth(process.env.REACT_APP_BASE_URL || ''),
  tagTypes: [SERVICE_FEE_TAG, MODULE_FIELDS_TAG],
  endpoints: (builder) => ({
    createAndUpdateServiceFee: builder.mutation<
      ServiceFeeResponse,
      CreateAndUpdateServiceFeeRequest
    >({
      query: (payload) => {
        // Check if this is an update request (has an id)
        if ('id' in payload) {
          return {
            url: `${ApiPath.updateServiceFee}/${payload.id}`,
            method: 'PUT',
            body: payload.data,
          };
        }
        // This is a create request
        return {
          url: ApiPath.createServiceFee,
          method: 'POST',
          body: payload.data,
        };
      },
      invalidatesTags: [SERVICE_FEE_TAG],
    }),

    getServiceFeeList: builder.query<
      any,
      {
        page: number;
        pageSize: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        search?: string;
        filters?: Array<{
          key: string;
          value: string;
        }>;
      }
    >({
      query: (params) => ({
        url: ApiPath.getServiceFeeList,
        method: 'POST',
        body: {
          page: params.page,
          pageSize: params.pageSize,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          search: params.search,
          filters: params.filters,
        },
      }),
      providesTags: [{ type: SERVICE_FEE_TAG } as const],
    }),

    getServiceFeeById: builder.query<
      ServiceFeeResponse,
      string
    >({
      query: (id) => ({
        url: `${ApiPath.getServiceFeeById}/${id}`,
        method: 'GET',
      }),
      providesTags: [SERVICE_FEE_TAG],
    }),

    getModuleList: builder.query<
      { status: string; message: string; data: string[] },
      void
    >({
      query: () => ({
        url: ApiPath.getModuleList,
        method: 'GET',
      }),
      providesTags: [{ type: SERVICE_FEE_TAG } as const],
    }),

    getModuleFields: builder.query<
      ModuleFieldsResponse,
      string
    >({
      query: (moduleName) => ({
        url: `${ApiPath.getModuleList}/${moduleName}`,
        method: 'GET',
      }),
      providesTags: (result, error, moduleName) => [
        { type: MODULE_FIELDS_TAG, id: moduleName },
      ],
    }),

    deleteServiceFee: builder.mutation<
      { status: string; message: string },
      string
    >({
      query: (id) => ({
        url: `${ApiPath.deleteServiceFee}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [SERVICE_FEE_TAG],
    }),
  }),
});

export const {
  useCreateAndUpdateServiceFeeMutation,
  useGetServiceFeeListQuery,
  useGetServiceFeeByIdQuery,
  useGetModuleListQuery,
  useLazyGetModuleFieldsQuery,
  useDeleteServiceFeeMutation,
} = ServiceFeeService;